import { useEffect, useState, useMemo } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import {
    Search, ChevronUp, ChevronDown, Network,
    ArrowLeft, ArrowRight, Shield, Briefcase, Users,
} from "lucide-react";

/* ── Role badge ─────────────────────────────────────────────────── */
function RoleBadge({ role }) {
    if (role === "admin")
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold
                bg-blue-50 text-blue-700 border border-blue-200">
                <Shield className="w-2.5 h-2.5" /> Admin
            </span>
        );
    if (role === "business")
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold
                bg-purple-50 text-purple-700 border border-purple-200">
                <Briefcase className="w-2.5 h-2.5" /> Business
            </span>
        );
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold
            bg-teal-50 text-teal-700 border border-teal-200">
            <Users className="w-2.5 h-2.5" /> Member
        </span>
    );
}

/* ── Side chip ──────────────────────────────────────────────────── */
function SideChip({ side }) {
    if (side === "L")
        return (
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[10px] font-bold
                bg-teal-50 text-teal-700">
                <ArrowLeft className="w-2.5 h-2.5" /> L
            </span>
        );
    if (side === "R")
        return (
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[10px] font-bold
                bg-rose-50 text-rose-600">
                R <ArrowRight className="w-2.5 h-2.5" />
            </span>
        );
    return <span className="text-slate-300 text-xs">—</span>;
}

/* ── Subtree balance bar ────────────────────────────────────────── */
function BalanceBar({ left, right }) {
    const total = left + right;
    const lPct  = total > 0 ? (left  / total) * 100 : 0;
    const rPct  = total > 0 ? (right / total) * 100 : 0;
    return (
        <div className="min-w-[120px]">
            <div className="flex justify-between text-[10px] font-bold mb-1">
                <span className="text-teal-600">{left} L</span>
                <span className="text-rose-500">R {right}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
                {total > 0 ? (
                    <>
                        <div className="h-full bg-teal-500 rounded-l-full" style={{ width: `${lPct}%` }} />
                        <div className="h-full bg-rose-500 rounded-r-full" style={{ width: `${rPct}%` }} />
                    </>
                ) : (
                    <div className="h-full w-full bg-slate-100 rounded-full" />
                )}
            </div>
        </div>
    );
}

/* ── Sort header ────────────────────────────────────────────────── */
function Th({ label, col, sortBy, sortDir, onSort, className = "" }) {
    const active = sortBy === col;
    return (
        <th
            onClick={() => onSort(col)}
            className={`px-4 py-3 text-left text-[10px] font-bold tracking-wider uppercase
                text-white/70 hover:text-white cursor-pointer select-none whitespace-nowrap ${className}`}
        >
            {label}
            {active
                ? (sortDir === "asc"
                    ? <ChevronUp   className="w-3 h-3 inline ml-1 text-sky-300" />
                    : <ChevronDown className="w-3 h-3 inline ml-1 text-sky-300" />)
                : null
            }
        </th>
    );
}

const ROLE_FILTERS = [
    { label: "All",      value: "all"      },
    { label: "Members",  value: "member"   },
    { label: "Admin",    value: "admin"    },
    { label: "Business", value: "business" },
];

export default function AdminNetworkTree() {
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [search,  setSearch]  = useState("");
    const [role,    setRole]    = useState("all");
    const [sortBy,  setSortBy]  = useState("left_subtree");
    const [sortDir, setSortDir] = useState("desc");

    useEffect(() => {
        api.get("/admin/network-overview")
            .then(r => setRows(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSort = (col) => {
        if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortBy(col); setSortDir("desc"); }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows
            .filter(r => role === "all" || r.role === role)
            .filter(r => !q || r.full_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q))
            .sort((a, b) => {
                const va = a[sortBy] ?? "", vb = b[sortBy] ?? "";
                const cmp = typeof va === "number"
                    ? va - vb
                    : String(va).localeCompare(String(vb));
                return sortDir === "asc" ? cmp : -cmp;
            });
    }, [rows, search, role, sortBy, sortDir]);

    const stats = useMemo(() => ({
        total:    rows.length,
        members:  rows.filter(r => r.role === "member").length,
        admins:   rows.filter(r => r.role === "admin").length,
        business: rows.filter(r => r.role === "business").length,
        maxL: Math.max(0, ...rows.map(r => r.left_subtree)),
        maxR: Math.max(0, ...rows.map(r => r.right_subtree)),
    }), [rows]);

    return (
        <AppShell>
            <div className="space-y-5 pb-12">

                {/* ── Header ── */}
                <div>
                    <div className="overline">Admin</div>
                    <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5 flex items-center gap-2.5">
                        Network Tree Overview
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
                            bg-teal-50 text-teal-700 border border-teal-100">
                            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                            All Users
                        </span>
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Left / right subtree counts, direct children, and tree placement for every user.
                    </p>
                </div>

                {/* ── Stats strip ── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                        { label: "Total Users",     val: stats.total,    color: "text-[#0C2B5C]" },
                        { label: "Members",         val: stats.members,  color: "text-teal-600"  },
                        { label: "Admins",          val: stats.admins,   color: "text-blue-600"  },
                        { label: "Business",        val: stats.business, color: "text-purple-600"},
                        { label: "Max Left Subtree",val: stats.maxL,     color: "text-teal-600"  },
                        { label: "Max Right Subtree",val: stats.maxR,    color: "text-rose-500"  },
                    ].map(({ label, val, color }) => (
                        <div key={label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                            <div className={`text-2xl font-extrabold ${color} tabular-nums`}>{val}</div>
                            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">{label}</div>
                        </div>
                    ))}
                </div>

                {/* ── Table card ── */}
                <div className="bg-white rounded-3xl shadow-sm overflow-hidden">

                    {/* Controls */}
                    <div className="px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center
                        justify-between gap-3 border-b border-slate-100">
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Search name or email…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-xl
                                        text-xs outline-none focus:ring-2 focus:ring-[#2172E5]/20
                                        focus:border-[#2172E5]/40 w-52 transition-all"
                                />
                            </div>

                            {/* Role filter */}
                            <div className="flex bg-slate-100 p-1 rounded-xl gap-0.5">
                                {ROLE_FILTERS.map(({ label, value }) => (
                                    <button
                                        key={value}
                                        onClick={() => setRole(value)}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all
                                            ${role === value
                                                ? "bg-white text-slate-800 shadow-sm"
                                                : "text-slate-500 hover:text-slate-700"
                                            }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <span className="text-xs text-slate-400 font-medium">
                            {filtered.length} of {rows.length} users
                        </span>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-[#0C2B5C]">
                                    <th className="px-4 py-3 text-[10px] font-bold tracking-wider uppercase
                                        text-white/70 text-center w-12">#</th>
                                    <Th label="Name"          col="full_name"    sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    <Th label="Email"         col="email"        sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    <th className="px-4 py-3 text-[10px] font-bold tracking-wider uppercase text-white/70">Role</th>
                                    <th className="px-4 py-3 text-[10px] font-bold tracking-wider uppercase text-white/70 text-center">Side</th>
                                    <Th label="Direct L"     col="direct_L"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-center" />
                                    <Th label="Direct R"     col="direct_R"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-center" />
                                    <Th label="L Subtree"    col="left_subtree"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-center" />
                                    <Th label="R Subtree"    col="right_subtree" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="text-center" />
                                    <th className="px-4 py-3 text-[10px] font-bold tracking-wider uppercase text-white/70">L / R Balance</th>
                                    <Th label="Tree Parent"  col="parent_name"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={11} className="py-16 text-center text-sm text-slate-400">
                                            <Network className="w-8 h-8 animate-pulse text-slate-300 mx-auto mb-2" />
                                            Loading network data…
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={11} className="py-16 text-center text-sm text-slate-400">
                                            No users match the current filter.
                                        </td>
                                    </tr>
                                ) : filtered.map((row, i) => (
                                    <tr key={row.id}
                                        className={`transition-colors duration-100 hover:bg-blue-50/30
                                            ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"}`}>

                                        {/* # */}
                                        <td className="px-4 py-3 text-center text-xs text-slate-300 font-mono">{i + 1}</td>

                                        {/* Name */}
                                        <td className="px-4 py-3">
                                            <div className="font-bold text-slate-800 whitespace-nowrap">{row.full_name}</div>
                                            <div className="font-mono text-[10px] text-[#2172E5] bg-blue-50
                                                px-1.5 py-0.5 rounded border border-blue-100 inline-block mt-0.5">
                                                {row.referral_code}
                                            </div>
                                        </td>

                                        {/* Email */}
                                        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{row.email}</td>

                                        {/* Role */}
                                        <td className="px-4 py-3"><RoleBadge role={row.role} /></td>

                                        {/* Side */}
                                        <td className="px-4 py-3 text-center"><SideChip side={row.side} /></td>

                                        {/* Direct L */}
                                        <td className="px-4 py-3 text-center tabular-nums">
                                            {row.direct_L > 0
                                                ? <span className="font-bold text-teal-600">{row.direct_L}</span>
                                                : <span className="text-slate-300">0</span>}
                                        </td>

                                        {/* Direct R */}
                                        <td className="px-4 py-3 text-center tabular-nums">
                                            {row.direct_R > 0
                                                ? <span className="font-bold text-rose-500">{row.direct_R}</span>
                                                : <span className="text-slate-300">0</span>}
                                        </td>

                                        {/* L Subtree */}
                                        <td className="px-4 py-3 text-center tabular-nums">
                                            {row.left_subtree > 0
                                                ? <span className="font-bold text-teal-600">{row.left_subtree}</span>
                                                : <span className="text-slate-300">0</span>}
                                        </td>

                                        {/* R Subtree */}
                                        <td className="px-4 py-3 text-center tabular-nums">
                                            {row.right_subtree > 0
                                                ? <span className="font-bold text-rose-500">{row.right_subtree}</span>
                                                : <span className="text-slate-300">0</span>}
                                        </td>

                                        {/* Balance bar */}
                                        <td className="px-4 py-3">
                                            <BalanceBar left={row.left_subtree} right={row.right_subtree} />
                                        </td>

                                        {/* Tree Parent */}
                                        <td className="px-4 py-3">
                                            {row.parent_name
                                                ? <span className="text-xs text-slate-500 whitespace-nowrap">{row.parent_name}</span>
                                                : <span className="text-xs text-slate-300">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between
                        text-xs text-slate-400 flex-wrap gap-2">
                        <span>
                            Showing <span className="font-bold text-slate-600">{filtered.length}</span> of{" "}
                            <span className="font-bold text-slate-600">{rows.length}</span> users
                        </span>
                        <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1.5 text-teal-600 font-semibold">
                                <span className="w-2 h-2 rounded-full bg-teal-500" /> Left wing
                            </span>
                            <span className="flex items-center gap-1.5 text-rose-500 font-semibold">
                                <span className="w-2 h-2 rounded-full bg-rose-400" /> Right wing
                            </span>
                            <span>Subtree = all active members in that leg · Direct = immediate children only</span>
                        </div>
                    </div>
                </div>

            </div>
        </AppShell>
    );
}
